import * as React from 'react';
import { View } from 'react-native';
import { useAnimatedStyle } from 'react-native-reanimated';
import { rem, Spacer } from 'design-system';
import BlurAmountView from 'design-system/BlurAmountView';
import Text, { styles as textStyles } from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { selectScrambled } from 'reducers/selectors';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector } from 'store/hooks';
import { selectCreditScoreBands } from '../../selectors';
import CreditScoreBandsTooltip from '../CreditScoreBandsTooltip';
import HeaderText from './HeaderText';
import { isWeb } from '../../../../constants';
const getScoreBand = (bands, score) => {
    const band = bands.find((b) => score >= b.min && score <= b.max);
    return band?.label ?? '';
};
const buildTexts = (data, scoreBands) => {
    const headerTexts = [];
    const headerBandTexts = [];
    const valueTexts = [];
    const signTexts = [];
    const suffixTexts = [];
    const sinceTexts = [];
    const deltaSigns = [];
    data.forEach((entry, i) => {
        const prev = i > 0 ? data[i - 1] : null;
        const band = getScoreBand(scoreBands, entry.value);
        const isCurrent = i === data.length - 1;
        headerTexts[i] = band
            ? `Your credit score ${isCurrent ? 'is' : 'was'}`
            : 'Score history';
        headerBandTexts[i] = band;
        valueTexts[i] = String(entry.value);
        if (prev) {
            const delta = entry.value - prev.value;
            let sign = '±';
            if (delta > 0)
                sign = '+';
            else if (delta < 0)
                sign = '';
            signTexts[i] = `${sign}${delta}`;
            suffixTexts[i] = ' points';
            sinceTexts[i] = ` from ${prev.label} to ${entry.label}`;
            deltaSigns[i] = Math.sign(delta);
        }
        else {
            signTexts[i] = '';
            suffixTexts[i] = '';
            sinceTexts[i] = 'Your journey started here!';
            deltaSigns[i] = 0;
        }
    });
    return {
        headerTexts,
        headerBandTexts,
        valueTexts,
        signTexts,
        suffixTexts,
        sinceTexts,
        deltaSigns,
    };
};
const CreditScoreHistoryHeader = ({ data, selectedIndex, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const scoreBands = useAppSelector(selectCreditScoreBands);
    const scrambled = useAppSelector(selectScrambled);
    const maxScore = scoreBands.reduce((max, b) => Math.max(max, b.max), 0);
    const { headerTexts, headerBandTexts, valueTexts, signTexts, suffixTexts, sinceTexts, deltaSigns, } = React.useMemo(() => buildTexts(data, scoreBands), [data, scoreBands]);
    const fallbackIndex = data.length > 0 ? data.length - 1 : 0;
    const fallbackHeader = headerTexts[fallbackIndex] ?? 'Credit score history';
    const fallbackHeaderBand = headerBandTexts[fallbackIndex] ?? '';
    const fallbackValue = valueTexts[fallbackIndex] ?? '';
    const fallbackSign = signTexts[fallbackIndex] ?? '';
    const fallbackSuffix = suffixTexts[fallbackIndex] ?? '';
    const fallbackSince = sinceTexts[fallbackIndex] ?? '';
    const positiveColor = colors.text.positive;
    const negativeColor = colors.text.negative;
    const neutralColor = colors.text.secondary;
    const deltaColorStyle = useAnimatedStyle(() => {
        const sign = deltaSigns[selectedIndex.value] ?? 0;
        let color = neutralColor;
        if (sign > 0)
            color = positiveColor;
        else if (sign < 0)
            color = negativeColor;
        return { color };
    });
    const signDisplayStyle = useAnimatedStyle(() => ({
        display: signTexts[selectedIndex.value] ? 'flex' : 'none',
    }));
    const suffixDisplayStyle = useAnimatedStyle(() => ({
        display: suffixTexts[selectedIndex.value] ? 'flex' : 'none',
    }));
    return (<View style={styles.container}>
      <View style={styles.titleContainer}>
        <HeaderText texts={headerTexts} selectedIndex={selectedIndex} fallback={fallbackHeader} style={styles.headerText}/>
        <Spacer w={4}/>
        <HeaderText scramblable texts={headerBandTexts} selectedIndex={selectedIndex} fallback={fallbackHeaderBand} style={styles.headerText} blurStyle={styles.headerTextBlur}/>
        <Spacer w={4}/>
        <CreditScoreBandsTooltip offset={4}/>
      </View>
      <View style={styles.valueRow}>
        <HeaderText scramblable texts={valueTexts} selectedIndex={selectedIndex} fallback={fallbackValue} style={styles.valueText} blurStyle={styles.valueTextBlur}/>
        <Spacer w={4}/>
        <Text Numbers-18 Secondary style={styles.maxScoreText}>
          {'/'}
        </Text>
        <Spacer w={4}/>
        <Text Numbers-18 Secondary style={styles.maxScoreText}>
          {maxScore}
        </Text>
      </View>
      <View style={styles.deltaRow}>
        <BlurAmountView enabled={scrambled} blurStyle={styles.deltaTextBlur}>
          <View style={styles.deltaGroup}>
            <HeaderText texts={signTexts} selectedIndex={selectedIndex} fallback={fallbackSign} style={[styles.signText, deltaColorStyle, signDisplayStyle]}/>
            <HeaderText texts={suffixTexts} selectedIndex={selectedIndex} fallback={fallbackSuffix} style={[styles.suffixText, deltaColorStyle, suffixDisplayStyle]}/>
          </View>
        </BlurAmountView>
        <HeaderText texts={sinceTexts} selectedIndex={selectedIndex} fallback={fallbackSince} style={styles.sinceText}/>
      </View>
    </View>);
};
export default React.memo(CreditScoreHistoryHeader);
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingHorizontal: rem(16),
        paddingBottom: rem(16),
    },
    titleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerText: {
        margin: 0,
        padding: 0,
        color: colors.text.secondary,
        fontSize: textStyles.Text_14.fontSize,
        lineHeight: textStyles.Text_14.lineHeight,
        fontFamily: textStyles.Text_14.fontFamily,
    },
    // Insets the scramble pill from the text's full line box down to roughly
    // the glyph height; must track headerText's lineHeight/fontSize.
    headerTextBlur: {
        top: (textStyles.Text_14.lineHeight - textStyles.Text_14.fontSize) / 2,
        bottom: (textStyles.Text_14.lineHeight - textStyles.Text_14.fontSize) / 2,
        marginBottom: rem(-1),
    },
    valueRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        paddingTop: rem(2),
        marginBottom: rem(-6),
    },
    maxScoreText: {
        marginBottom: rem(6),
    },
    valueText: {
        margin: 0,
        padding: 0,
        color: colors.text.primary,
        fontSize: textStyles.Numbers_36.fontSize,
        lineHeight: rem(48),
        fontFamily: textStyles.Numbers_36.fontFamily,
    },
    // Insets the scramble pill from the text's full line box down to roughly
    // the glyph height; must track valueText's lineHeight/fontSize.
    valueTextBlur: {
        top: (rem(48) - textStyles.Numbers_36.fontSize) / 2,
        bottom: (rem(48) - textStyles.Numbers_36.fontSize) / 2,
    },
    deltaRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        height: textStyles.Text_14.lineHeight,
        gap: isWeb ? rem(4) : undefined,
    },
    // Reproduces sign/suffix's previous direct-child layout in deltaRow so the
    // shared scramble pill can wrap them as one group.
    deltaGroup: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: isWeb ? rem(4) : undefined,
    },
    deltaTextBlur: {
        top: (textStyles.Numbers_14.lineHeight - textStyles.Numbers_14.fontSize) / 2,
        bottom: (textStyles.Numbers_14.lineHeight - textStyles.Numbers_14.fontSize) / 2,
        marginBottom: rem(-1),
    },
    signText: {
        margin: 0,
        padding: 0,
        fontSize: textStyles.Numbers_14.fontSize,
        lineHeight: textStyles.Numbers_14.lineHeight,
        fontFamily: textStyles.Numbers_14.fontFamily,
        marginBottom: rem(-1.8),
    },
    suffixText: {
        margin: 0,
        padding: 0,
        fontSize: textStyles.Text_14.fontSize,
        lineHeight: textStyles.Text_14.lineHeight,
        fontFamily: textStyles.Text_14.fontFamily,
    },
    sinceText: {
        margin: 0,
        padding: 0,
        color: colors.text.secondary,
        fontSize: textStyles.TextThin_14.fontSize,
        lineHeight: textStyles.TextThin_14.lineHeight,
        fontFamily: textStyles.TextThin_14.fontFamily,
    },
}));
